"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { sendGTMEvent } from "@next/third-parties/google";
import { Loader2, Search, Sparkles } from "lucide-react";
interface SimpleSearchProps {
  className?: string;
  placeholder?: string;
  showExample?: boolean;
}
interface SearchSuggestion {
  type: "make" | "model" | "bodyStyle" | "fuel" | "keyword";
  value: string;
  label: string;
  href?: string;
  count?: number;
}
export default function SimpleSearch({
  className = "",
  placeholder = "Search trucks with red interior, SUVs with pano roof...",
  showExample = true
}: SimpleSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const router = useRouter();
  const inputId = useId();
  const listboxId = `${inputId}-suggestions`;
  const statusId = `${inputId}-status`;
  const abortControllerRef = useRef<AbortController | null>(null);
  const exampleSearch = "show me all SUVs with sunroof";
  const trimmedQuery = searchQuery.trim();
  const shouldShowSuggestions = isFocused && trimmedQuery.length >= 2 && (isLoading || suggestions.length > 0 || hasLoadedSuggestions);
  const activeSuggestion = activeIndex >= 0 ? suggestions[activeIndex] : undefined;
  const submitSearch = (query: string, source = "hero_search") => {
    const searchParams = new URLSearchParams();
    const trimmedQuery_0 = query.trim();
    if (trimmedQuery_0) {
      searchParams.set("q", trimmedQuery_0);

      // Track search event
      try {
        sendGTMEvent({
          event: "search",
          search_term: trimmedQuery_0,
          page_section: source,
          user_intent: "browse"
        });
      } catch (error) {
        console.error("Analytics tracking error:", error);
      }
    }
    const url = `/cars${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    router.push(url);
  };
  const selectSuggestion = (suggestion: SearchSuggestion) => {
    setSearchQuery(suggestion.value);
    setSuggestions([]);
    setActiveIndex(-1);
    setIsFocused(false);
    try {
      sendGTMEvent({
        event: "search_suggestion_select",
        search_term: suggestion.value,
        suggestion_type: suggestion.type,
        page_section: "hero_search",
        user_intent: "browse"
      });
    } catch (error_0) {
      console.error("Analytics tracking error:", error_0);
    }
    router.push(suggestion.href ?? `/cars?q=${encodeURIComponent(suggestion.value)}`);
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeSuggestion) {
      selectSuggestion(activeSuggestion);
      return;
    }
    submitSearch(searchQuery);
  };
  const handleExampleSearch = () => {
    setSearchQuery(exampleSearch);
    submitSearch(exampleSearch, "hero_ai_example");
  };
  const handleKeyDown = (e_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (!shouldShowSuggestions) return;
    if (e_0.key === "ArrowDown") {
      e_0.preventDefault();
      setActiveIndex(currentIndex => {
        if (suggestions.length === 0) return -1;
        return currentIndex >= suggestions.length - 1 ? 0 : currentIndex + 1;
      });
    } else if (e_0.key === "ArrowUp") {
      e_0.preventDefault();
      setActiveIndex(currentIndex_0 => {
        if (suggestions.length === 0) return -1;
        return currentIndex_0 <= 0 ? suggestions.length - 1 : currentIndex_0 - 1;
      });
    } else if (e_0.key === "Escape") {
      setSuggestions([]);
      setActiveIndex(-1);
      setHasLoadedSuggestions(false);
    }
  };
  const getSuggestionTypeLabel = (type: SearchSuggestion["type"]) => {
    if (type === "bodyStyle") return "Style";
    if (type === "fuel") return "Fuel";
    if (type === "make") return "Make";
    if (type === "model") return "Model";
    return "Search";
  };
  useEffect(() => {
    abortControllerRef.current?.abort();
    if (trimmedQuery.length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      setIsLoading(false);
      setHasLoadedSuggestions(false);
      return;
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsLoading(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/search/suggestions?q=${encodeURIComponent(trimmedQuery)}&limit=8`, {
          signal: abortController.signal
        });
        if (!response.ok) {
          throw new Error(`Suggestion request failed with ${response.status}`);
        }
        const data = (await response.json()) as {
          suggestions?: SearchSuggestion[];
        };
        setSuggestions(data.suggestions ?? []);
        setActiveIndex(-1);
        setHasLoadedSuggestions(true);
      } catch (error_1) {
        if ((error_1 as Error).name !== "AbortError") {
          console.error("Search suggestion error:", error_1);
          setSuggestions([]);
          setHasLoadedSuggestions(true);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 220);
    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [trimmedQuery]);
  return <form data-kiso-ignore onSubmit={handleSearch} className={`t-w-full t-space-y-3 ${className}`}>
            <div className="t-relative">
                <div className="t-flex t-items-center t-overflow-hidden t-bg-white t-shadow-[0_8px_25px_rgba(0,0,0,0.18)]">
                    <div className="t-flex t-items-center t-pl-3 sm:t-pl-4">
                        <Search className="t-w-5 t-h-5 t-text-gray-500" aria-hidden="true" />
                    </div>
                    <input id={inputId} type="text" role="combobox" value={searchQuery} onChange={e_1 => setSearchQuery(e_1.target.value)} onFocus={() => setIsFocused(true)} onBlur={() => window.setTimeout(() => setIsFocused(false), 120)} onKeyDown={handleKeyDown} placeholder={placeholder} className="t-min-w-0 t-flex-1 t-px-2 t-py-4 t-text-base t-bg-transparent t-border-none t-outline-none placeholder:t-text-[11px] sm:t-px-4 sm:placeholder:t-text-base placeholder:t-text-gray-500 t-text-gray-900" autoComplete="off" aria-autocomplete="list" aria-controls={listboxId} aria-describedby={statusId} aria-expanded={shouldShowSuggestions} aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined} aria-busy={isLoading} aria-label="Search by vehicle type, color, feature, or exact model" />
                    {isLoading && <Loader2 className="t-mr-3 t-h-4 t-w-4 t-shrink-0 t-animate-spin t-text-gray-500" aria-hidden="true" />}
                    <button type="submit" className="t-flex t-min-w-14 t-shrink-0 t-items-center t-justify-center t-self-stretch t-bg-automotive-primary t-px-4 t-font-extrabold t-text-white t-transition-colors hover:t-bg-automotive-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-inset focus:t-ring-white sm:t-min-w-20 sm:t-px-6" aria-label="Search for cars">
                        Go
                    </button>
                </div>

                <p id={statusId} className="t-sr-only" aria-live="polite">
                    {isLoading ? "Loading vehicle search suggestions." : suggestions.length > 0 ? `${suggestions.length} vehicle search suggestions available. Use arrow keys to review them.` : hasLoadedSuggestions ? "No vehicle search suggestions found. Press Enter to search for your typed query." : ""}
                </p>

                {shouldShowSuggestions && <div className="t-absolute t-left-0 t-right-0 t-top-[calc(100%+0.5rem)] t-z-30 t-overflow-hidden t-rounded-xl t-border t-border-slate-200 t-bg-white t-shadow-2xl">
                        {suggestions.length > 0 ? <ul id={listboxId} role="listbox" aria-label="Vehicle search suggestions" className="t-m-0 t-max-h-80 t-list-none t-overflow-y-auto t-p-1">
                                {suggestions.map((suggestion_0, index) => <li key={`${suggestion_0.type}-${suggestion_0.value}`} className="t-m-0 t-p-0">
                                        <div id={`${listboxId}-option-${index}`} data-hero-search-option role="option" tabIndex={-1} aria-selected={activeIndex === index} onMouseDown={event => event.preventDefault()} onClick={() => selectSuggestion(suggestion_0)} style={activeIndex === index ? {
              backgroundColor: "#1C1D1F",
              boxShadow: "inset 4px 0 0 #1C1D1F",
              color: "#ffffff",
              outline: "2px solid #1C1D1F",
              outlineOffset: "-2px"
            } : undefined} className={`t-flex t-w-full t-cursor-pointer t-items-center t-justify-between t-gap-3 t-rounded-lg t-px-3 t-py-3 t-text-left t-text-sm t-transition-colors ${activeIndex === index ? "t-bg-automotive-primary t-text-white" : "t-bg-white t-text-slate-900 hover:t-bg-slate-100"}`}>
                                            <span className="t-min-w-0">
                                                <span className="t-block t-truncate t-font-semibold">{suggestion_0.label}</span>
                                                <span className={`t-block t-text-xs ${activeIndex === index ? "t-text-white" : "t-text-slate-600"}`}>
                                                    {getSuggestionTypeLabel(suggestion_0.type)}
                                                </span>
                                            </span>
                                            {typeof suggestion_0.count === "number" && <span data-hero-search-count style={activeIndex === index ? {
                backgroundColor: "#ffffff",
                color: "#1C1D1F"
              } : undefined} className={`t-shrink-0 t-rounded-full t-px-2.5 t-py-1 t-text-xs t-font-semibold ${activeIndex === index ? "t-bg-white t-text-automotive-primary" : "t-bg-slate-100 t-text-slate-700"}`}>
                                                    {suggestion_0.count} {suggestion_0.count === 1 ? "car" : "cars"}
                                                </span>}
                                        </div>
                                    </li>)}
                            </ul> : <div className="t-px-4 t-py-3 t-text-sm t-font-medium t-text-slate-700">
                                Press Enter to search for "{trimmedQuery}"
                            </div>}
                    </div>}

                <style jsx global>{`
                    [data-hero-search-option][aria-selected="true"] {
                        background-color: #1C1D1F !important;
                        color: #ffffff !important;
                    }

                    [data-hero-search-option][aria-selected="true"] [data-hero-search-count] {
                        background-color: #ffffff !important;
                        color: #1C1D1F !important;
                    }
                `}</style>
            </div>

            {showExample && <button type="button" onClick={handleExampleSearch} className="t-flex t-w-full t-items-start sm:t-items-center t-gap-2 t-rounded-xl t-border t-border-white/20 t-bg-white/10 t-px-4 t-py-3 t-text-left t-text-sm sm:t-text-base t-font-medium t-text-white t-backdrop-blur-sm t-transition-colors hover:t-bg-white/20 focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-yellow focus:t-ring-offset-2 focus:t-ring-offset-automotive-primary" aria-label={`Try this search example: ${exampleSearch}`}>
                    <Sparkles className="t-mt-0.5 sm:t-mt-0 t-h-4 t-w-4 t-shrink-0 t-text-brand-yellow" aria-hidden="true" />
                    <span>
                        Try this search: <span className="t-text-brand-yellow">{exampleSearch}</span>
                    </span>
                </button>}
        </form>;
}